const COMMON_TIMEZONES = [
  "UTC",
  "Asia/Kuwait",
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Qatar",
  "Asia/Bahrain",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
];

export const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const getTimezoneOffsetLabel = (timezone: string, date = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    return (
      formatter
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    return "";
  }
};

export const formatTimezoneLabel = (timezone: string) => {
  const offset = getTimezoneOffsetLabel(timezone);
  return offset ? `${timezone} (${offset})` : timezone;
};

export const isValidTimezone = (timezone: string) => {
  if (!timezone) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

const getAllTimezones = () => {
  if (typeof Intl.supportedValuesOf === "function") {
    return Intl.supportedValuesOf("timeZone");
  }

  return COMMON_TIMEZONES;
};

export type TimezoneOption = {
  value: string;
  label: string;
};

export const buildTimezoneOptions = (
  preferredTimezones: string[] = [],
): TimezoneOption[] => {
  const browserTimezone = getBrowserTimezone();
  const priority = new Set(
    [browserTimezone, ...preferredTimezones, ...COMMON_TIMEZONES].filter(
      isValidTimezone,
    ),
  );

  const remaining = getAllTimezones()
    .filter((timezone) => isValidTimezone(timezone) && !priority.has(timezone))
    .sort((left, right) => left.localeCompare(right));

  return Array.from(priority).concat(remaining).map((timezone) => ({
    value: timezone,
    label: formatTimezoneLabel(timezone),
  }));
};
